"use client";

import { Check, Loader2 } from "lucide-react";
import { FOLLOW_LOCATIONS } from "@punchup/data/legacy";
import {
  useVenueSubscription,
  useVenueSubscriptionMutation,
} from "../../hooks/venuePages/useVenueSubscription";

import CheckoutModalOrDrawerVenue from "./CheckoutModalOrDrawerVenue";
import { VenuePagesRow } from "../../data/venue_pages";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { logException } from "@punchup/sentry";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useState } from "react";

const valuePropositions = [
  "First access to tickets",
  "Invites to special events",
  "Exclusive deals & giveaways",
];

export interface VenueSubscriptionBannerProps {
  venue: VenuePagesRow;
  userEmail?: string;
  isUserSubscribed: boolean;
  bannerColor?: string;
  buttonLabelColor?: string;
  buttonBackgroundColor?: string;
}

export default function VenueSubscriptionBanner({
  venue,
  userEmail,
  isUserSubscribed,
  bannerColor = "#F5F0E8",
  buttonLabelColor = "#FFFFFF",
  buttonBackgroundColor = "#29333C",
}: VenueSubscriptionBannerProps) {
  const [email, setEmail] = useState(userEmail || "");
  const [displayLoggedOutModal, setDisplayLoggedOutModal] = useState(false);
  const { isFollowing } = useVenueSubscription(venue.id, isUserSubscribed);
  const mutation = useVenueSubscriptionMutation(venue);
  const supabase = createClientComponentClient();

  const handleSubscribe = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        setDisplayLoggedOutModal(true);
        return;
      }

      mutation.mutate(
        { action: "follow", location: FOLLOW_LOCATIONS.VENUE_PAGE },
        {
          onSuccess: () => {
            toast({
              title: `Subscribed to ${venue.name}`,
              description: `You will now receive updates from ${venue.name}`,
              variant: "success",
            });
          },
          onError: (error) => {
            logException(error);
            toast({
              title: "Error",
              description: "An error occurred while trying to subscribe",
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      logException(error);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className="relative flex-1 px-4 py-6 overflow-hidden border rounded-xl text-primary-blue md:px-10 md:py-16 lg:px-20 border-text-secondary"
        style={{ backgroundColor: bannerColor }}
      >
        {/* Decorative elements */}
        <div className="absolute w-4 h-4 rounded-full top-4 right-8 bg-white/20"></div>
        <div className="absolute w-2 h-2 rounded-full top-12 right-16 bg-white/30"></div>
        <div className="absolute w-3 h-3 rounded-full bottom-8 left-12 bg-white/25"></div>
        <div className="absolute w-5 h-5 rounded-full bottom-16 right-24 bg-white/15"></div>

        <div className="relative z-10 flex flex-col max-w-2xl mx-auto text-center gap-y-2">
          {isFollowing ? (
            <div className="flex flex-col items-center justify-center gap-3 py-4 md:py-8">
              <Check
                width={48}
                height={48}
                strokeWidth={2}
                className="text-green-600"
              />
              <h2 className="text-2xl font-bold md:text-3xl">
                You&apos;re on the guest list!
              </h2>
              <p className="text-sm md:text-base text-secondary-blue leading-5">
                You&apos;ll receive updates about upcoming shows and events from{" "}
                <span className="whitespace-nowrap">{venue.name}</span>
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-y-1 md:gap-y-4">
                <h2 className="text-2xl font-bold md:text-3xl">
                  Join <span className="whitespace-nowrap">the</span>{" "}
                  <span className="whitespace-nowrap">guest list</span>
                </h2>
                <p className="text-sm md:text-base text-secondary-blue leading-5">
                  Stay up-to-date on the latest from{" "}
                  <span className="whitespace-nowrap">{venue.name}</span>
                </p>
              </div>
              <ul className="flex items-start flex-col gap-1 md:gap-1.5 pt-1 md:pt-2 mx-auto w-fit">
                {valuePropositions.map((valueProp, index) => (
                  <li key={index} className="flex flex-row items-center gap-2">
                    <Check
                      width={16}
                      height={16}
                      strokeWidth={3}
                      className="text-green-600"
                    />
                    <p className="text-sm text-center md:text-base">
                      {valueProp}
                    </p>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-3 mt-2 md:mt-4 md:max-w-md md:mx-auto sm:flex-row md:flex-col 2xl:flex-row sm:gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={mutation.isPending}
                  className="flex-1 text-black border-0 bg-white/90 placeholder:text-gray-500 focus:ring-2 focus:ring-white/20 md:h-[42px] px-3 py-3 rounded-md"
                />
                <button
                  onClick={handleSubscribe}
                  disabled={mutation.isPending || !email.trim()}
                  className="flex flex-row items-center justify-center gap-2 px-8 py-3 font-semibold border-none rounded-lg sm:min-w-[120px] h-[50px] md:h-[42px]"
                  style={{
                    color: buttonLabelColor,
                    backgroundColor: buttonBackgroundColor,
                  }}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2
                        width={16}
                        height={16}
                        className="animate-spin"
                      />
                      Subscribing...
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <CheckoutModalOrDrawerVenue
        isOpen={displayLoggedOutModal}
        setIsOpen={setDisplayLoggedOutModal}
        venueToFollow={venue}
        redirectTo={
          process.env.NEXT_PUBLIC_IS_EXTERNAL_SITE ? "/" : `/v/${venue.slug}`
        }
        userEmail={email}
      />
    </div>
  );
}
